/**
 * Product Category Selector
 * Integrates with existing theme variant picker system
 * Follows existing theme patterns exactly
 */

class HDTCategorySelector extends HTMLElement {
  constructor() {
    super();
    this.productId = this.getAttribute('product-id');
    this.formId = this.getAttribute('form');
    this.isEmbroideredTemplate = this.checkEmbroideredTemplate();
    this.init();
  }

  init() {
    this.sizeSection = this.querySelector('.hdt-size-selection');
    this.shawlSection = this.querySelector('.hdt-shawl-selection');
    this.shawlTypeSection = this.querySelector('.hdt-shawl-type-selection');
    this.isUnstitchedTemplate = this.checkUnstitchedTemplate();
    
    this.bindEvents();
    this.initializeFlow();
    this.setupFormValidation();
    
    // Force price update check after initialization
    setTimeout(() => {
      this.updateTotalPrice();
    }, 500);
  }

  initializeFlow() {
    // Progressive flow: Initially show only category
    if (this.sizeSection) {
      this.sizeSection.style.display = 'none';
    }
    if (this.shawlSection) {
      this.shawlSection.style.display = 'none';
    }
    if (this.shawlTypeSection) {
      this.shawlTypeSection.style.display = 'none';
    }
  }

  bindEvents() {
    // Handle category selection (follows theme's variant picker pattern)
    this.querySelectorAll('[data-category]').forEach(element => {
      element.addEventListener('click', (e) => {
        // Always prevent default to handle everything manually
        e.preventDefault();
        e.stopPropagation();
        this.handleCategorySelection(element);
      });
    });

    // Handle size selection
    this.querySelectorAll('[data-size]').forEach(element => {
      element.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSizeSelection(element);
      });
    });

    // Handle shawl selection
    this.querySelectorAll('[data-shawl]').forEach(element => {
      element.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleShawlSelection(element);
      });
    });

    // Handle shawl type selection (for unstitched template)
    this.querySelectorAll('[data-shawl-type]').forEach(element => {
      element.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleShawlTypeSelection(element);
      });
    });

    // Handle popover selections (dropdown mode)  
    this.querySelectorAll('hdt-richlist button[role="option"]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (button.hasAttribute('data-category')) {
          this.handleCategorySelection(button);
        } else if (button.hasAttribute('data-size')) {
          this.handleSizeSelection(button);
        } else if (button.hasAttribute('data-shawl')) {
          this.handleShawlSelection(button);
        } else if (button.hasAttribute('data-shawl-type')) {
          this.handleShawlTypeSelection(button);
        }
      });
    });
    
    // Prevent radio buttons from being unchecked by direct click
    this.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.addEventListener('click', (e) => {
        // If it's already checked, prevent it from being unchecked
        if (radio.checked) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    });
  }

  handleCategorySelection(element) {
    const category = element.getAttribute('data-category');
    const value = element.getAttribute('data-value');
    const price = element.getAttribute('data-price') || '0';
    const fieldset = element.closest('fieldset');
    
    // Update selected state following theme pattern
    this.updateSelectedState(fieldset, element, value);
    
    // Update hidden input for form submission
    this.updateHiddenInput(fieldset, 'properties[Category]', value);
    this.updateHiddenInput(fieldset, 'properties[Category Price]', price);
    
    // Clear validation errors on selection
    this.clearValidationErrorsIfValid();
    
    // Update total pricing
    this.updateTotalPrice();
    
    // Progressive flow logic
    if (category === 'stitched') {
      // Case 1: Show size selection, hide shawl until size is selected
      this.showSizeSection();
      this.hideShawlSection();
      this.clearSizeSelection();
      this.clearShawlSelection();
    } else if (category === 'unstitched') {
      // Case 2: Show shawl directly, hide size section
      this.hideSizeSection();
      this.showShawlSection();
      this.clearSizeSelection();
      this.clearShawlSelection();
    }
  }

  handleSizeSelection(element) {
    const size = element.getAttribute('data-size');
    const value = element.getAttribute('data-value');
    const fieldset = element.closest('fieldset');
    
    // Update selected state following theme pattern
    this.updateSelectedState(fieldset, element, value);
    
    // Update hidden input for form submission
    this.updateHiddenInput(fieldset, 'properties[Size]', value);
    
    // Clear validation errors on selection
    this.clearValidationErrorsIfValid();
    
    // Show shawl section after size selection (for stitched products, unless embroidered)
    if (!this.isEmbroideredTemplate) {
      this.showShawlSection();
      this.clearShawlSelection();
    }
  }

  handleShawlSelection(element) {
    const shawl = element.getAttribute('data-shawl');
    const value = element.getAttribute('data-value');
    const fieldset = element.closest('fieldset');
    
    // Update selected state following theme pattern
    this.updateSelectedState(fieldset, element, value);
    
    // Update hidden input for form submission
    this.updateHiddenInput(fieldset, 'properties[Shawl]', value);
    
    // Clear validation errors on selection
    this.clearValidationErrorsIfValid();

    // Show shawl type selection when "with" is selected (for all templates)
    if (shawl === 'with') {
      this.showShawlTypeSection();
      this.clearShawlTypeSelection();
    } else if (shawl === 'without') {
      this.hideShawlTypeSection();
      this.clearShawlTypeSelection();
    }
  }

  updateSelectedState(fieldset, selectedElement, value) {
    // Find and check the correct radio button first
    let targetRadio = null;
    let targetLabel = null;
    
    if (selectedElement.classList.contains('hdt-product-form__value')) {
      // Direct label click
      targetLabel = selectedElement;
      targetRadio = selectedElement.querySelector('input[type="radio"]');
    } else {
      // For popover options, find corresponding label
      const dataAttr = selectedElement.hasAttribute('data-category') ? 'data-category' : 
                      selectedElement.hasAttribute('data-size') ? 'data-size' : 
                      selectedElement.hasAttribute('data-shawl') ? 'data-shawl' : 'data-shawl-type';
      const dataValue = selectedElement.getAttribute(dataAttr);
      targetLabel = fieldset.querySelector(`[${dataAttr}="${dataValue}"].hdt-product-form__value`);
      if (targetLabel) {
        targetRadio = targetLabel.querySelector('input[type="radio"]');
      }
    }
    
    // Uncheck all radio buttons first
    fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
    });
    
    // Remove all selected states first
    fieldset.querySelectorAll('.hdt-product-form__value').forEach(option => {
      option.classList.remove('is--selected');
    });
    
    // Now set the target radio button and label
    if (targetRadio) {
      targetRadio.checked = true;
      targetRadio.setAttribute('checked', 'checked');
      
      // Force the browser to recognize the change
      targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
      targetRadio.dispatchEvent(new Event('input', { bubbles: true }));
    }
    
    if (targetLabel) {
      targetLabel.classList.add('is--selected');
      targetLabel.setAttribute('data-selected', 'true');
    }
    
    // Double-check after a microtask
    requestAnimationFrame(() => {
      if (targetRadio) {
        targetRadio.checked = true;
      }
      if (targetLabel) {
        targetLabel.classList.add('is--selected');
      }
    });
    
    // Update display text (follows theme pattern)
    const updateValueSpan = fieldset.querySelector('[update-value]');
    if (updateValueSpan) {
      updateValueSpan.textContent = value;
    }
  }

  updateHiddenInput(fieldset, name, value) {
    const hiddenInput = fieldset.querySelector(`input[name="${name}"]`);
    if (hiddenInput) {
      hiddenInput.value = value;
    }
  }

  // Progressive flow helper methods
  showSizeSection() {
    if (this.sizeSection) {
      this.sizeSection.style.display = 'block';
    }
  }

  hideSizeSection() {
    if (this.sizeSection) {
      this.sizeSection.style.display = 'none';
    }
  }

  showShawlSection() {
    if (this.shawlSection) {
      this.shawlSection.style.display = 'block';
    }
  }

  hideShawlSection() {
    if (this.shawlSection) {
      this.shawlSection.style.display = 'none';
    }
  }

  showShawlTypeSection() {
    if (this.shawlTypeSection) {
      this.shawlTypeSection.style.display = 'block';
    }
  }

  hideShawlTypeSection() {
    if (this.shawlTypeSection) {
      this.shawlTypeSection.style.display = 'none';
    }
  }

  clearSizeSelection() {
    if (this.sizeSection) {
      const sizeRadios = this.sizeSection.querySelectorAll('input[type="radio"]');
      sizeRadios.forEach(radio => radio.checked = false);
      
      const sizeLabels = this.sizeSection.querySelectorAll('.hdt-product-form__value');
      sizeLabels.forEach(label => label.classList.remove('is--selected'));
      
      // Reset hidden input
      const hiddenInput = this.sizeSection.querySelector('input[name="properties[Size]"]');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
      
      // Reset display text
      const updateValueSpan = this.sizeSection.querySelector('[update-value]');
      if (updateValueSpan) {
        updateValueSpan.textContent = 'Choose size';
      }
    }
  }

  clearShawlSelection() {
    if (this.shawlSection) {
      const shawlRadios = this.shawlSection.querySelectorAll('input[type="radio"]');
      shawlRadios.forEach(radio => radio.checked = false);
      
      const shawlLabels = this.shawlSection.querySelectorAll('.hdt-product-form__value');
      shawlLabels.forEach(label => label.classList.remove('is--selected'));
      
      // Reset hidden input
      const hiddenInput = this.shawlSection.querySelector('input[name="properties[Shawl]"]');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
      
      // Reset display text
      const updateValueSpan = this.shawlSection.querySelector('[update-value]');
      if (updateValueSpan) {
        updateValueSpan.textContent = 'Choose shawl option';
      }
      
      // Clear shawl type selection as well
      this.clearShawlTypeSelection();
      this.hideShawlTypeSection();
      
      // Update pricing and reset display
      this.updateTotalPrice();
      this.resetPriceDisplay();
    }
  }

  resetPriceDisplay() {
    // Reset price display to original
    const priceElements = document.querySelectorAll('[data-original-price]');
    priceElements.forEach(priceElement => {
      const originalPrice = priceElement.getAttribute('data-original-price');
      if (originalPrice) {
        priceElement.textContent = originalPrice;
        priceElement.removeAttribute('data-original-price');
      }
    });

    // Remove custom price display
    const customPriceElement = document.getElementById('hdt-custom-extra-price');
    if (customPriceElement) {
      customPriceElement.remove();
    }
  }

  findMainPriceElements() {
    // Extended list of possible price selectors for different themes
    const mainPriceSelectors = [
      '.product__price .price',
      '.product-price .price', 
      '.hdt-price .price',
      '.price-item .price',
      '.product__price',
      '.product-price',
      '.price__current',
      '.current-price',
      '.price',
      '.money',
      '.product__price-and-badge .price',
      '.product-form__price .price',
      '.product-info__price .price',
      '.price-list .price',
      '.variant-price',
      '.price-regular',
      '.price__regular',
      '.price-current',
      '.price__sale',
      '.price-sale',
      '[data-price]',
      '.product-price-current',
      '.product-regular-price',
      '.shopify-price'
    ];
    
    // Try each selector
    for (const selector of mainPriceSelectors) {
      const elements = document.querySelectorAll(selector);
      
      for (const element of elements) {
        const text = element.textContent.trim();
        
        if (text && (text.includes('Rs') || text.includes('₨') || text.match(/\d+[,\d]*\.?\d*/))) {
          return [element];
        }
      }
    }
    
    // More comprehensive fallback search
    const allElements = document.querySelectorAll('*');
    const priceElements = [];
    
    allElements.forEach((element, index) => {
      const text = element.textContent?.trim();
      const tagName = element.tagName.toLowerCase();
      
      // Skip script, style, and non-visible elements
      if (!text || 
          ['script', 'style', 'noscript'].includes(tagName) ||
          element.offsetParent === null ||
          element.closest('.hdt-category-selector')) {
        return;
      }
      
      // Look for price patterns
      const pricePatterns = [
        /Rs\s*[\d,]+(\.\d{2})?/i,
        /₨\s*[\d,]+(\.\d{2})?/i,
        /PKR\s*[\d,]+(\.\d{2})?/i,
        /\$\s*[\d,]+(\.\d{2})?/,
        /^\s*[\d,]+(\.\d{2})?\s*Rs/i,
        /^\s*[\d,]+(\.\d{2})?\s*₨/i
      ];
      
      const matchesPrice = pricePatterns.some(pattern => pattern.test(text));
      
      if (matchesPrice && !element.querySelector('*')) { // Leaf elements only
        priceElements.push(element);
      }
    });
    
    // Filter to most likely candidates
    const filteredElements = priceElements.filter(element => {
      const text = element.textContent.trim();
      const classList = element.className;
      
      // Prefer elements with price-related classes
      const hasPriceClass = /price|money|cost|amount/i.test(classList);
      // Prefer elements that are just numbers with currency
      const isSimplePrice = /^[Rs₨$\s]*[\d,]+(\.\d{2})?[Rs₨$\s]*$/i.test(text);
      
      return hasPriceClass || isSimplePrice;
    });
    
    return filteredElements.slice(0, 2); // Return max 2 elements
  }

  clearShawlTypeSelection() {
    if (this.shawlTypeSection) {
      const shawlTypeRadios = this.shawlTypeSection.querySelectorAll('input[type="radio"]');
      shawlTypeRadios.forEach(radio => radio.checked = false);
      
      const shawlTypeLabels = this.shawlTypeSection.querySelectorAll('.hdt-product-form__value');
      shawlTypeLabels.forEach(label => label.classList.remove('is--selected'));
      
      // Reset hidden input
      const hiddenInput = this.shawlTypeSection.querySelector('input[name="properties[Shawl Type]"]');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
      
      // Reset display text
      const updateValueSpan = this.shawlTypeSection.querySelector('[update-value]');
      if (updateValueSpan) {
        updateValueSpan.textContent = 'Choose shawl type';
      }
    }
  }

  handleShawlTypeSelection(element) {
    const shawlType = element.getAttribute('data-shawl-type');
    const value = element.getAttribute('data-value');
    const price = element.getAttribute('data-price') || '0';
    const fieldset = element.closest('fieldset');
    
    // Update selected state following theme pattern
    this.updateSelectedState(fieldset, element, value);
    
    // Update hidden input for form submission
    this.updateHiddenInput(fieldset, 'properties[Shawl Type]', value);
    this.updateHiddenInput(fieldset, 'properties[Shawl Type Price]', price);
    
    // Clear validation errors on selection
    this.clearValidationErrorsIfValid();

    // Also update the main shawl value to reflect the specific type chosen
    const mainShawlInput = this.shawlSection?.querySelector('input[name="properties[Shawl]"]');
    if (mainShawlInput) {
      mainShawlInput.value = value; // Use the specific shawl type as the main value
    }

    // Calculate total additional price
    this.updateTotalPrice();
  }

  updateTotalPrice() {
    let totalExtraPrice = 0;
    
    // Get category price
    const categoryPriceInput = this.querySelector('input[name="properties[Category Price]"]');
    if (categoryPriceInput && categoryPriceInput.value) {
      totalExtraPrice += parseFloat(categoryPriceInput.value) || 0;
    }
    
    // Get shawl type price
    const shawlTypePriceInput = this.querySelector('input[name="properties[Shawl Type Price]"]');
    if (shawlTypePriceInput && shawlTypePriceInput.value) {
      totalExtraPrice += parseFloat(shawlTypePriceInput.value) || 0;
    }
    
    // Store total price in a hidden input for cart
    let totalPriceInput = this.querySelector('input[name="properties[Total Extra Price]"]');
    if (!totalPriceInput) {
      totalPriceInput = document.createElement('input');
      totalPriceInput.type = 'hidden';
      totalPriceInput.name = 'properties[Total Extra Price]';
      totalPriceInput.setAttribute('form', this.formId);
      this.appendChild(totalPriceInput);
    }
    totalPriceInput.value = `Rs ${totalExtraPrice}`;
    
    // Update product subtotal display
    this.updateSubtotalDisplay(totalExtraPrice);
    
    return totalExtraPrice;
  }

  updateSubtotalDisplay(extraPrice) {
    // Reset any previously modified prices first
    this.resetPriceDisplay();
    
    if (extraPrice <= 0) {
      return; // No extra price to show
    }
    
    // Find main price elements
    const priceElements = this.findMainPriceElements();
    
    if (priceElements.length === 0) {
      this.createCustomPriceDisplay(extraPrice);
      return;
    }
    
    this.updatePriceElements(priceElements, extraPrice);
  }

  createCustomPriceDisplay(extraPrice) {
    // Remove any existing custom price display
    const existingCustomPrice = document.getElementById('hdt-custom-extra-price');
    if (existingCustomPrice) {
      existingCustomPrice.remove();
    }

    // Create a custom price display element
    const customPriceElement = document.createElement('div');
    customPriceElement.id = 'hdt-custom-extra-price';
    customPriceElement.style.cssText = `
      background: #f0f9ff;
      border: 2px solid #0ea5e9;
      border-radius: 8px;
      padding: 12px 16px;
      margin: 15px 0;
      color: #0c4a6e;
      font-weight: 600;
      font-size: 16px;
      text-align: center;
    `;
    customPriceElement.innerHTML = `
      <span style="color: #0c4a6e;">� Additional Charges: Rs ${extraPrice}</span>
      <div style="font-size: 14px; font-weight: normal; margin-top: 4px; color: #64748b;">
        Included in the total price shown above
      </div>
    `;

    // Try to insert near the product form or price area
    const insertTargets = [
      '.product-form',
      '.product__form', 
      '.hdt-product-form',
      '.product-price',
      '.product__price',
      '.variant-picker',
      '.product-variant-picker',
      this.closest('form'),
      this
    ];

    let inserted = false;
    for (const targetSelector of insertTargets) {
      const target = typeof targetSelector === 'string' 
        ? document.querySelector(targetSelector) 
        : targetSelector;
        
      if (target) {
        target.insertAdjacentElement('afterend', customPriceElement);
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      // Fallback: insert at the end of the category selector
      this.appendChild(customPriceElement);
    }
  }

  updatePriceElements(priceElements, extraPrice) {
    priceElements.forEach((priceElement, index) => {
      const originalText = priceElement.textContent.trim();
      
      // Store original price if not already stored
      if (!priceElement.hasAttribute('data-original-price')) {
        priceElement.setAttribute('data-original-price', originalText);
      }
      
      // Extract the base price from original text
      const storedOriginal = priceElement.getAttribute('data-original-price');
      const priceMatch = storedOriginal.match(/[\d,]+/);
      
      if (priceMatch) {
        const basePrice = parseFloat(priceMatch[0].replace(/,/g, ''));
        const totalPrice = basePrice + extraPrice;
        
        // Format the total price with commas if original had them
        const formattedTotal = storedOriginal.includes(',') 
          ? totalPrice.toLocaleString('en-PK')
          : totalPrice.toString();
        
        // Replace the price with the new total, keeping the currency symbol
        const newPriceText = storedOriginal.replace(/[\d,]+/, formattedTotal);
        priceElement.textContent = newPriceText;
      }
    });
  }



  getCurrentSelections() {
    const categoryElement = this.querySelector('[data-category].is--selected');
    const sizeElement = this.querySelector('[data-size].is--selected');
    const shawlElement = this.querySelector('[data-shawl].is--selected');
    const shawlTypeElement = this.querySelector('[data-shawl-type].is--selected');
    
    return {
      category: categoryElement ? categoryElement.getAttribute('data-category') : null,
      size: sizeElement ? sizeElement.getAttribute('data-size') : null,
      shawl: shawlElement ? shawlElement.getAttribute('data-shawl') : null,
      shawlType: shawlTypeElement ? shawlTypeElement.getAttribute('data-shawl-type') : null
    };
  }

  setupFormValidation() {
    // Find the product form
    const form = document.getElementById(this.formId) || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Find all possible purchase buttons (Add to Cart, Buy it Now, etc.)
    const purchaseButtons = form.querySelectorAll(`
      [name="add"],
      [type="submit"],
      .btn-product-add,
      .btn-buy-now,
      .shopify-payment-button,
      .shopify-payment-button *,
      .dynamic-checkout__content,
      .dynamic-checkout__content *,
      button[name="add"],
      button[type="submit"],
      input[name="add"],
      input[type="submit"],
      [class*="buy"],
      [class*="purchase"],
      [class*="checkout"],
      [data-testid*="Checkout"],
      [data-testid*="PaymentButton"],
      iframe[src*="checkouts"]
    `);

    // Only validate on regular Add to Cart button click
    const addToCartButtons = form.querySelectorAll('[name="add"]:not(.shopify-payment-button)');
    
    addToCartButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        if (!this.validateSelections()) {
          e.preventDefault();
          e.stopPropagation();
          this.showValidationErrors();
          return false;
        }
      });
    });

    // Also listen for dynamic payment buttons that might be added later
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check for Shopify payment buttons
            const dynamicButtons = node.querySelectorAll(`
              .shopify-payment-button,
              .shopify-payment-button *,
              .dynamic-checkout__content,
              .dynamic-checkout__content *,
              [data-testid*="Checkout"],
              [data-testid*="PaymentButton"],
              [class*="buy-now"],
              iframe[src*="checkouts"]
            `);
            
            dynamicButtons.forEach(button => {
              ['click', 'mousedown', 'touchstart'].forEach(eventType => {
                button.addEventListener(eventType, (e) => {
                  if (!this.validateSelections()) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    this.showValidationErrors();
                    return false;
                  }
                }, { capture: true, passive: false });
              });
            });

            // Also check if the node itself is a payment button
            if (node.matches && node.matches('.shopify-payment-button, .dynamic-checkout__content, [class*="buy-now"]')) {
              ['click', 'mousedown', 'touchstart'].forEach(eventType => {
                node.addEventListener(eventType, (e) => {
                  if (!this.validateSelections()) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    this.showValidationErrors();
                    return false;
                  }
                }, { capture: true, passive: false });
              });
            }
          }
        });
      });
    });

    // Observe the form for dynamically added buttons
    observer.observe(form, { childList: true, subtree: true });

    // Listen for Shopify-specific payment events
    document.addEventListener('shopify:payment_button:clicked', (e) => {
      if (!this.validateSelections()) {
        e.preventDefault();
        e.stopPropagation();
        this.showValidationErrors();
        return false;
      }
    });

    // Listen for cart events
    document.addEventListener('cart:add', (e) => {
      if (!this.validateSelections()) {
        e.preventDefault();
        e.stopPropagation();
        this.showValidationErrors();
        return false;
      }
    });

    // Make validation available globally for theme integration
    window.validateCategorySelector = () => {
      return this.validateSelections();
    };

    window.showCategorySelectorErrors = () => {
      this.showValidationErrors();
    };

    // Aggressive global interception for Buy it Now buttons
    this.setupGlobalInterception();
  }

  setupGlobalInterception() {
    // Intercept all clicks at document level to catch Buy it Now buttons
    document.addEventListener('click', (e) => {
      const target = e.target;
      
      // Check if the clicked element or its parents are payment buttons
      const isPaymentButton = target.closest(`
        .shopify-payment-button,
        .dynamic-checkout__content,
        [class*="buy-now"],
        [class*="buynow"],
        [data-shopify*="payment"],
        [data-shopify*="checkout"],
        [data-testid*="Checkout"],
        [data-testid*="PaymentButton"]
      `);

      if (isPaymentButton) {
        if (!this.validateSelections()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.showValidationErrors();
          return false;
        }
      }
    }, { capture: true, passive: false });

    // Also intercept beforeunload and other navigation events
    window.addEventListener('beforeunload', (e) => {
      if (!this.validateSelections() && document.activeElement && 
          document.activeElement.closest('.shopify-payment-button, .dynamic-checkout__content')) {
        e.preventDefault();
        e.returnValue = 'Please complete all product selections before purchasing.';
        return 'Please complete all product selections before purchasing.';
      }
    });
  }

  validateSelections() {
    const selectedCategory = this.getSelectedCategory();
    
    // For embroidered template with only stitched option, auto-select if none selected
    if (this.isEmbroideredTemplate && !selectedCategory) {
      const stitchedOption = this.querySelector('[data-category="stitched"]');
      if (stitchedOption) {
        this.handleCategorySelection(stitchedOption);
        return this.validateSelections(); // Re-validate after auto-selection
      }
    }
    
    // Category is always required
    if (!selectedCategory) {
      return false;
    }

    // If stitched is selected, size is required
    if (selectedCategory === 'stitched') {
      const selectedSize = this.getSelectedSize();
      if (!selectedSize) {
        return false;
      }
    }

    // Shawl selection is required unless it's embroidered template
    if (!this.isEmbroideredTemplate) {
      const selectedShawl = this.getSelectedShawl();
      if (!selectedShawl) {
        return false;
      }

      // If "with shawl" is selected, shawl type is required (for any template)
      if (selectedShawl === 'with') {
        const selectedShawlType = this.getSelectedShawlType();
        if (!selectedShawlType) {
          return false;
        }
      }
    }

    return true;
  }

  getSelectedCategory() {
    const categoryRadio = this.querySelector('input[name="category-selector"]:checked');
    if (categoryRadio) {
      return categoryRadio.value;
    }
    
    // Fallback: check for is--selected class on labels
    const selectedLabel = this.querySelector('[data-category].is--selected');
    if (selectedLabel) {
      return selectedLabel.getAttribute('data-category');
    }
    
    return null;
  }

  getSelectedSize() {
    const sizeRadio = this.querySelector('input[name="size-selector"]:checked');
    if (sizeRadio) {
      return sizeRadio.value;
    }
    
    // Fallback: check for is--selected class on labels
    const selectedLabel = this.querySelector('[data-size].is--selected');
    if (selectedLabel) {
      return selectedLabel.getAttribute('data-size');
    }
    
    return null;
  }

  getSelectedShawl() {
    const shawlRadio = this.querySelector('input[name="shawl-selector"]:checked');
    if (shawlRadio) {
      return shawlRadio.value;
    }
    
    // Fallback: check for is--selected class on labels
    const selectedLabel = this.querySelector('[data-shawl].is--selected');
    if (selectedLabel) {
      return selectedLabel.getAttribute('data-shawl');
    }
    
    return null;
  }

  getSelectedShawlType() {
    const shawlTypeRadio = this.querySelector('input[name="shawl-type-selector"]:checked');
    if (shawlTypeRadio) {
      return shawlTypeRadio.value;
    }
    
    // Fallback: check for is--selected class on labels
    const selectedLabel = this.querySelector('[data-shawl-type].is--selected');
    if (selectedLabel) {
      return selectedLabel.getAttribute('data-shawl-type');
    }
    
    return null;
  }

  showValidationErrors() {
    // Remove existing error messages
    this.removeValidationErrors();

    // Add invalid class to disable purchase buttons
    this.classList.add('hdt-category-selector-invalid');

    const selectedCategory = this.getSelectedCategory();
    
    // Show category error if not selected
    if (!selectedCategory) {
      const errorMessage = this.isEmbroideredTemplate 
        ? 'Please select Stitched category' 
        : 'Please select a category (Stitched or Unstitched)';
      this.showFieldError(this.querySelector('fieldset[data-index="category"]'), errorMessage);
    }

    // Show size error if stitched is selected but no size chosen
    if (selectedCategory === 'stitched') {
      const selectedSize = this.getSelectedSize();
      if (!selectedSize) {
        this.showFieldError(this.querySelector('fieldset[data-index="size"]'), 'Please select a size');
      }
    }

    // Show shawl error if no shawl option selected (unless embroidered template)
    if (!this.isEmbroideredTemplate) {
      const selectedShawl = this.getSelectedShawl();
      if (!selectedShawl) {
        this.showFieldError(this.querySelector('fieldset[data-index="shawl"]'), 'Please select a shawl option');
      } else if (selectedShawl === 'with') {
        // Show shawl type error if "with shawl" selected but no type specified
        const selectedShawlType = this.getSelectedShawlType();
        if (!selectedShawlType) {
          this.showFieldError(this.querySelector('fieldset[data-index="shawl-type"]'), 'Please select a shawl type (Wool or Velvet)');
        }
      }
    }

    // Scroll to first error
    const firstError = this.querySelector('.hdt-category-error');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  showFieldError(fieldset, message) {
    if (!fieldset) return;

    const errorDiv = document.createElement('div');
    errorDiv.className = 'hdt-category-error hdt-form-error';
    errorDiv.style.cssText = `
      background: #fee2e2;
      border: 1px solid #fca5a5;
      border-radius: 6px;
      padding: 12px 16px;
      margin: 8px 0;
      color: #dc2626;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      animation: shake 0.5s ease-in-out;
    `;
    errorDiv.innerHTML = `
      <span class="hdt-error-icon" style="font-size: 16px;">⚠️</span>
      <span class="hdt-error-message" style="font-weight: 500;">${message}</span>
    `;
    
    fieldset.appendChild(errorDiv);
    fieldset.classList.add('has-error');
  }

  removeValidationErrors() {
    this.querySelectorAll('.hdt-category-error').forEach(error => error.remove());
    this.querySelectorAll('fieldset.has-error').forEach(fieldset => {
      fieldset.classList.remove('has-error');
    });
    // Remove invalid class to re-enable purchase buttons
    this.classList.remove('hdt-category-selector-invalid');
  }

  clearValidationErrorsIfValid() {
    // Only clear errors if all selections are now valid
    if (this.validateSelections()) {
      this.removeValidationErrors();
      this.showSuccessMessage();
    }
  }

  showSuccessMessage() {
    // Remove any existing success message
    const existingSuccess = this.querySelector('.hdt-category-success');
    if (existingSuccess) {
      existingSuccess.remove();
    }

    // Only show success if all selections are made
    if (this.validateSelections()) {
      const successDiv = document.createElement('div');
      successDiv.className = 'hdt-category-success';
      successDiv.style.cssText = `
        background: #dcfce7;
        border: 1px solid #86efac;
        border-radius: 6px;
        padding: 12px 16px;
        margin: 8px 0;
        color: #166534;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      
      const totalPrice = this.updateTotalPrice();
      successDiv.innerHTML = `
        <span style="font-size: 16px;">✅</span>
        <span style="font-weight: 500;">Ready to add to cart! Extra cost: Rs ${totalPrice}</span>
      `;
      
      this.appendChild(successDiv);
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.remove();
        }
      }, 3000);
    }
  }

  checkEmbroideredTemplate() {
    // Check if using embroidered template
    return window.location.pathname.includes('embroidered-template') || 
           document.body.classList.contains('template-product-embroidered-template') ||
           document.querySelector('[data-template="embroidered"]');
  }

  checkUnstitchedTemplate() {
    // Check if using unstitched template
    const isUnstitched = window.location.pathname.includes('unstitched-template') || 
           document.body.classList.contains('template-product-unstitched-template') ||
           document.querySelector('[data-template="unstitched"]') ||
           document.body.getAttribute('data-template') === 'product.unstitched-template';
    
    return isUnstitched;
  }
}

// Register the custom element
customElements.define('hdt-category-selector', HDTCategorySelector);

// Additional integration with existing variant picker
document.addEventListener('DOMContentLoaded', function() {
  // Listen for category changes to integrate with existing variant picker
  document.addEventListener('category:change', function(event) {
    const { category, size, shawl, productId } = event.detail;
    
    // Find the corresponding variant picker
    const variantPicker = document.querySelector(`hdt-variant-picker[handle*="${productId}"], hdt-variant-picker[selected-variant*="${productId}"]`);
    
    if (variantPicker) {
      const variantMapping = buildVariantMapping(category, size, shawl);
      updateVariantPicker(variantPicker, variantMapping);
    }
  });
});

/**
 * Build variant mapping based on category selections
 * This creates a standardized format for variant options
 */
function buildVariantMapping(category, size, shawl) {
  const mapping = [];
  
  // Add category
  mapping.push(category === 'stitched' ? 'Stitched' : 'Unstitched');
  
  // Add size only for stitched products
  if (category === 'stitched') {
    // Convert size to proper case
    const sizeMap = {
      'small': 'Small',
      'medium': 'Medium', 
      'large': 'Large',
      'xl': 'XL'
    };
    mapping.push(sizeMap[size] || size);
  }
  
  // Always include shawl option
  const shawlMap = {
    'with': 'With Shawl',
    'without': 'Without Shawl',
    'wool': 'Wool Shawl',
    'velvet': 'Velvet Shawl'
  };
  mapping.push(shawlMap[shawl] || shawl);
  
  return mapping;
}

/**
 * Update the existing variant picker based on category selections
 * This integrates with the theme's existing variant picker system
 */
function updateVariantPicker(variantPicker, variantMapping) {
  // This would need to be implemented based on the specific variant picker structure
  // For now, we'll dispatch a custom event that can be handled by the theme
  const event = new CustomEvent('variant:update-from-category', {
    detail: {
      variantMapping: variantMapping,
      picker: variantPicker
    },
    bubbles: true
  });
  
  variantPicker.dispatchEvent(event);
}